import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';

const baseSectionMappings: Record<string, string> = {
  customer: 'customer',
  billTo: 'billTo',
  pickup: 'pickup',
  consignee: 'consignee',
  specifications: 'specifications',
  rateData: 'rates',
  operator: 'operator',
  carrier: 'carrier',
  commodities: 'specifications',
  poNums: 'customer',
  mode: 'specifications',
  stops: 'pickup',
  additionalReferences: 'additionalReferences',
};

const tmsSpecificSectionOverrides: Partial<
  Record<string, Partial<Record<string, string>>>
> = {
  [TMS.ThreeG]: {
    additionalReferences: 'customer',
    commodities: 'items',
  },
  [TMS.PLS]: {
    commodities: 'items',
    stops: 'route',
    // Legacy create-load paths before Route UI; still expand Route if they appear.
    pickup: 'route',
    consignee: 'route',
    plsSelectedLocationId: 'customer',
    accessorials: 'specifications',
    plsOperationalFields: 'operationalFields',
    plsLoadServiceTypeId: 'optional',
    plsPendingReferences: 'optional',
  },
  [TMS.Aljex]: {
    specifications: 'commodities',
  },
  [TMS.GlobalTranzTMS]: {
    commodities: 'shipmentItems',
  },
  [TMS.TransportPro]: {
    stops: 'stops',
  },
  [TMS.MercurygateBoomi]: {
    rateData: 'rateData',
    commodities: 'commodities',
    specifications: 'commodities',
    accessorials: 'commodities',
    additionalReferences: 'references',
    notes: 'notes',
    stops: 'route',
    pickup: 'pickup',
    consignee: 'consignee',
  },
  [TMS.McleodEnterprise]: {
    // Nested route fields live under stops.N; resolved per-path below.
    stops: 'route',
  },
  [TMS.Mcleod]: {
    stops: 'route',
  },
  [TMS.Turvo]: {
    stops: 'route',
    pickup: 'route',
    consignee: 'route',
  },
};

/** Exact field-path overrides when the top-level key alone maps to the wrong accordion. */
const tmsSpecificPathOverrides: Partial<
  Record<string, Partial<Record<string, string>>>
> = {
  [TMS.PLS]: {
    // Ref # and service type live under Optional, not Customer.
    'customer.refNumber': 'optional',
  },
};

/** Nested accordion: child tab names that require a parent tab to be open. */
const nestedAccordionParentTabs: Partial<
  Record<string, Partial<Record<string, string>>>
> = {
  [TMS.MercurygateBoomi]: {
    pickup: 'route',
    consignee: 'route',
  },
  [TMS.Aljex]: {
    pickup: 'route',
    consignee: 'route',
  },
};

function isMcleodTms(tmsName?: string): boolean {
  return tmsName === TMS.McleodEnterprise || tmsName === TMS.Mcleod;
}

function mapMcleodStopFieldPathToAccordionSection(
  fieldPath: string
): Maybe<string> {
  if (/^stops\.\d+/.test(fieldPath)) {
    return 'route';
  }

  return null;
}

/**
 * Maps field paths to their corresponding accordion section names.
 * This is used to automatically expand accordion sections that contain validation errors.
 *
 * @param fieldPath - The field path from react-hook-form (e.g., "customer.externalTMSID")
 * @returns The accordion section name (e.g., "customer") or null if no mapping exists
 *
 * @example
 * mapFieldPathToAccordionSection('customer.externalTMSID') // → 'customer'
 * mapFieldPathToAccordionSection('rateData.customerLineItems.0.label') // → 'rates'
 * mapFieldPathToAccordionSection('specifications.minTempFahrenheit') // → 'specifications'
 * mapFieldPathToAccordionSection('commodities.0.description') // → 'specifications'
 */
export function mapFieldPathToAccordionSection(
  fieldPath: string,
  tmsName?: string
): Maybe<string> {
  const pathOverrides = tmsName ? tmsSpecificPathOverrides[tmsName] : undefined;
  const pathOverride = pathOverrides?.[fieldPath];
  if (pathOverride) {
    return pathOverride;
  }

  if (isMcleodTms(tmsName)) {
    const mcleodStopSection =
      mapMcleodStopFieldPathToAccordionSection(fieldPath);
    if (mcleodStopSection) {
      return mcleodStopSection;
    }
  }

  const topLevel = fieldPath.split('.')[0];

  const tmsOverrides = tmsName
    ? tmsSpecificSectionOverrides[tmsName]
    : undefined;
  const section = tmsOverrides?.[topLevel] ?? baseSectionMappings[topLevel];
  return section ?? null;
}

/**
 * Gets a unique list of accordion section names that need to be expanded
 * based on the error field paths.
 *
 * @param errorPaths - Array of field paths that have validation errors
 * @returns Array of unique accordion section names that should be expanded
 *
 * @example
 * getRequiredAccordionSections([
 *   'customer.externalTMSID',
 *   'customer.refNumber',
 *   'specifications.minTempFahrenheit',
 *   'rateData.customerLineItems.0.label'
 * ])
 * // → ['customer', 'specifications', 'rates']
 */
export function getRequiredAccordionSections(
  errorPaths: string[],
  tmsName?: string
): string[] {
  const sections = new Set<string>();

  for (const path of errorPaths) {
    const section = mapFieldPathToAccordionSection(path, tmsName);
    if (section) {
      sections.add(section);
    }
  }

  const parentTabs = tmsName ? nestedAccordionParentTabs[tmsName] : undefined;
  if (parentTabs) {
    for (const section of Array.from(sections)) {
      const parent = parentTabs[section];
      if (parent) {
        sections.add(parent);
      }
    }
  }

  return Array.from(sections);
}
